import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/lib/supabase";
import { authService } from "@/services/api";
import {
  initFirebase,
  getMessaging,
  getToken,
  onMessage,
} from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";

interface PushNotificationContextValue {
  /** Whether push notifications are supported and enabled. */
  enabled: boolean;
  /** Whether the user has granted notification permission. */
  permitted: boolean;
  /** Whether the FCM token has been registered with the server. */
  registered: boolean;
  /** Manually request permission and register the FCM token. */
  register: () => Promise<void>;
}

const PushNotificationContext = createContext<
  PushNotificationContextValue | undefined
>(undefined);

const env = import.meta.env as any;
const VAPID_KEY = env.VITE_FIREBASE_VAPID_KEY || "";

export function PushNotificationProvider({
  children,
}: { children: ReactNode }) {
  const { user, profile } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [permitted, setPermitted] = useState(false);
  const [registered, setRegistered] = useState(
    !!(profile as any)?.fcm_token,
  );

  // Initialize Firebase messaging.
  useEffect(() => {
    if (!env.VITE_FIREBASE_API_KEY) {
      setEnabled(false);
      return;
    }
    const messaging = initFirebase();
    if (messaging) {
      setEnabled(true);

      // Listen for foreground messages (when the tab is open).
      const unsubscribe = onMessage(messaging, (payload: any) => {
        const notification = payload.notification || {};
        if (notification.title) {
          new Notification(notification.title, {
            body: notification.body,
            icon: notification.icon || "/favicon.svg",
            data: payload.data || {},
          });
        }
      });
      return unsubscribe;
    }
  }, []);

  // Request permission and register FCM token when the user signs in.
  const register = useCallback(async () => {
    if (
      !user ||
      !("serviceWorker" in navigator) ||
      !("PushManager" in window)
    ) {
      return;
    }

    try {
      // Request notification permission.
      const permission = await Notification.requestPermission();
      setPermitted(permission === "granted");
      if (permission !== "granted") {
        return;
      }

      // Register the service worker.
      const registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js",
        { scope: "/" },
      );

      // Send Firebase config to the service worker.
      if (env.VITE_FIREBASE_API_KEY && registration.active) {
        registration.active.postMessage({
          type: "FIREBASE_CONFIG",
          config: {
            apiKey: env.VITE_FIREBASE_API_KEY,
            authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
            projectId: env.VITE_FIREBASE_PROJECT_ID,
            storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
            messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
            appId: env.VITE_FIREBASE_APP_ID,
            measurementId: env.VITE_FIREBASE_MEASUREMENT_ID,
          },
        });
      }

      // Get the FCM token.
      let messaging = getMessaging();
      if (!messaging) {
        initFirebase();
        messaging = getMessaging();
      }
      if (!messaging) return;

      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY || undefined,
        serviceWorkerRegistration: registration,
      });

      if (token) {
        // Store the token in the profiles table.
        const { error } = await (supabase as any)
          .from("profiles")
          .update({ fcm_token: token })
          .eq("id", user.id);
        if (!error) {
          setRegistered(true);
        }
      }
    } catch (error) {
      console.warn("Push notification registration failed:", error);
    }
  }, [user, profile, supabase]);

  // Auto-register when the user signs in.
  useEffect(() => {
    if (user && enabled && !registered) {
      void register();
    }
  }, [user, enabled, registered, register]);

  return (
    <PushNotificationContext.Provider
      value={{ enabled, permitted, registered, register }}
    >
      {children}
    </PushNotificationContext.Provider>
  );
}

export function usePushNotification() {
  const context = useContext(PushNotificationContext);
  if (!context) {
    throw new Error(
      "usePushNotification must be used within a PushNotificationProvider",
    );
  }
  return context;
}
